import { useEffect, useState, useRef, ComponentType } from "react";
import { motion, AnimatePresence } from "motion/react";
import {
  Sparkles,
  Compass,
  Users,
  Zap,
  Heart,
  Flame,
  Layers,
  Activity,
  Radio,
  BookMarked,
  Search,
  X,
  CornerDownLeft,
  Navigation
} from "lucide-react";

interface CosmicQuickJumpProps {
  language: "en" | "hr";
  onJump?: (sectionId: string) => void;
}

interface SectionItem {
  id: string;
  nameEn: string;
  nameHr: string;
  descEn: string;
  descHr: string;
  icon: ComponentType<{ className?: string }>;
}

const SECTIONS: SectionItem[] = [
  {
    id: "intro-section",
    nameEn: "1. Who are the Pleiadians?",
    nameHr: "1. Tko su Plejadani?",
    descEn: "Astronomical, mythological, and esoteric identity of the Seven Sisters",
    descHr: "Astronomski, mitološki i ezoterični identitet Sedam Sestara",
    icon: Sparkles
  },
  {
    id: "history-section",
    nameEn: "2. History Across Cultures",
    nameHr: "2. Povijest Kroz Kulture",
    descEn: "Ancient civilizations, Lascaux caves, and modern esotericism",
    descHr: "Drevne civilizacije, spilja Lascaux i moderna ezoterika",
    icon: Compass
  },
  {
    id: "clans-section",
    nameEn: "3. Pleiadian Clans & Archetypes",
    nameHr: "3. Plejadanski Klanovi i Arhetipovi",
    descEn: "Social structure, Law of One, and the Seven Star Clans",
    descHr: "Društvena struktura, Zakon Jednog i Sedam Zvjezdanih Klanova",
    icon: Users
  },
  {
    id: "tech-desc-section",
    nameEn: "4. Pleiadian Technology",
    nameHr: "4. Tehnologija Plejada",
    descEn: "Frequency physics, bioresonant starships, and crystal-scalar energy",
    descHr: "Frekvencijska fizika, biorezonantni brodovi i kristalno-skalarna energija",
    icon: Zap
  },
  {
    id: "humanity-section",
    nameEn: "5. Relationship with Humanity",
    nameHr: "5. Povezanost s Čovječanstvom",
    descEn: "DNA codes, megalithic alignments, and Starseeds psychological profile",
    descHr: "DNK kodovi, megalitska poravnanja i psihološki profil kozmičkog sjemena",
    icon: Heart
  },
  {
    id: "comparison-section",
    nameEn: "6. The Lyra–Orion Catalyst",
    nameHr: "6. Rat Lyra–Orion: Kozmičko Raskršće",
    descEn: "Lyran–Orion War, Fall of Lyra, Exodus & galactic turning point",
    descHr: "Lirsko-Orionski rat, Pad Lyre, Egzodus i kozmička prekretnica Galaktike",
    icon: Flame
  },
  {
    id: "evolution-section",
    nameEn: "7. Evolutionary Models",
    nameHr: "7. Evolucijski Modeli",
    descEn: "Dimension density scales, shadow integration, and 8Hz Schumann resonance",
    descHr: "Skala dimenzijskih gustoća, integracija sjene i 8Hz Schumannova rezonancija",
    icon: Activity
  },
  {
    id: "interactive-portal",
    nameEn: "8. Resources & Interactive Portal",
    nameHr: "8. Resursi i Interaktivni Portal",
    descEn: "Cosmic Star Map and Real-Time Frequency Synthesizer Controls",
    descHr: "Kozmička zvjezdana karta i sintesajzer frekvencija u realnom vremenu",
    icon: Radio
  },
  {
    id: "library-section",
    nameEn: "9. Pleiadian Galactic Library",
    nameHr: "9. Plejadanska Galaktička Knjižnica",
    descEn: "10 Books of Fragments, 9 Omega Black Documents, and Lyran Archives",
    descHr: "10 Knjiga Fragmenata, 9 Omega Black Dokumenata i Lirski Arhiv",
    icon: BookMarked
  }
];

export default function CosmicQuickJump({ language, onJump }: CosmicQuickJumpProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const resultsContainerRef = useRef<HTMLDivElement>(null);

  // Toggle modal on pressing 'K' or 'k' (or Ctrl+K / Meta+K)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignore if user is typing in form controls
      const activeEl = document.activeElement as HTMLElement | null;
      if (
        activeEl &&
        (activeEl.tagName === "INPUT" ||
          activeEl.tagName === "TEXTAREA" ||
          activeEl.isContentEditable)
      ) {
        // Allow escaping out of inputs
        if (e.key === "Escape" && isOpen) {
          setIsOpen(false);
        }
        return;
      }

      if (e.key.toLowerCase() === "k" || (e.key.toLowerCase() === "k" && (e.ctrlKey || e.metaKey))) {
        e.preventDefault();
        setIsOpen((prev) => !prev);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen]);

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen) {
      setSearchQuery("");
      setSelectedIndex(0);
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  // Filter sections based on search query
  const filteredSections = SECTIONS.filter((section) => {
    const query = searchQuery.toLowerCase();
    const name = (language === "en" ? section.nameEn : section.nameHr).toLowerCase();
    const desc = (language === "en" ? section.descEn : section.descHr).toLowerCase();
    return name.includes(query) || desc.includes(query);
  });

  // Handle arrow key navigation inside the modal
  useEffect(() => {
    if (!isOpen || filteredSections.length === 0) return;

    const handleNav = (e: KeyboardEvent) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIndex((prev) => (prev + 1) % filteredSections.length);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIndex((prev) => (prev - 1 + filteredSections.length) % filteredSections.length);
      } else if (e.key === "Enter") {
        e.preventDefault();
        const selected = filteredSections[selectedIndex];
        if (selected) {
          triggerJump(selected.id);
        }
      } else if (e.key === "Escape") {
        e.preventDefault();
        setIsOpen(false);
      }
    };

    window.addEventListener("keydown", handleNav);
    return () => window.removeEventListener("keydown", handleNav);
  }, [isOpen, selectedIndex, filteredSections]);

  // Scroll active item into view
  useEffect(() => {
    if (resultsContainerRef.current) {
      const activeItem = resultsContainerRef.current.children[selectedIndex] as HTMLElement;
      if (activeItem) {
        const container = resultsContainerRef.current;
        const itemTop = activeItem.offsetTop;
        const itemBottom = itemTop + activeItem.offsetHeight;
        const containerTop = container.scrollTop;
        const containerBottom = containerTop + container.offsetHeight;

        if (itemTop < containerTop) {
          container.scrollTop = itemTop;
        } else if (itemBottom > containerBottom) {
          container.scrollTop = itemBottom - container.offsetHeight;
        }
      }
    }
  }, [selectedIndex]);

  const triggerJump = (sectionId: string) => {
    setIsOpen(false);
    if (onJump) {
      onJump(sectionId);
    } else {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "start" });
        // Highlight effect
        element.classList.add("ring-2", "ring-[var(--quantum-turquoise)]/50", "transition-all", "duration-1000");
        setTimeout(() => {
          element.classList.remove("ring-2", "ring-[var(--quantum-turquoise)]/50");
        }, 2000);
      }
    }
  };

  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-10">
            {/* Backdrop Blur Overlay */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsOpen(false)}
              className="absolute inset-0 bg-[#04060C]/90 backdrop-blur-md"
            />

            {/* Modal HUD box */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: -20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ duration: 0.25, ease: "easeOut" }}
              className="relative w-full max-w-2xl bg-[#090E1A]/95 border border-quantum-turquoise/45 rounded-2xl shadow-2xl p-6 overflow-hidden flex flex-col max-h-[80vh]"
            >
              {/* Decorative HUD corners */}
              <div className="absolute top-2 left-2 w-4 h-4 border-t border-l border-quantum-turquoise"></div>
              <div className="absolute top-2 right-2 w-4 h-4 border-t border-r border-quantum-turquoise"></div>
              <div className="absolute bottom-2 left-2 w-4 h-4 border-b border-l border-quantum-turquoise"></div>
              <div className="absolute bottom-2 right-2 w-4 h-4 border-b border-r border-quantum-turquoise"></div>

              {/* Modal Header */}
              <div className="flex items-center justify-between pb-4 border-b border-slate-800/80 mb-4">
                <div className="flex items-center gap-2">
                  <Navigation className="w-5 h-5 text-quantum-turquoise animate-pulse" />
                  <div>
                    <h3 className="text-sm font-display font-bold tracking-[0.2em] uppercase text-white">
                      {language === "en" ? "COSMIC QUICK-JUMP" : "BRZI KOZMIČKI SKOK"}
                    </h3>
                    <p className="text-[9px] font-mono uppercase text-celestial-light/60 tracking-wider">
                      {language === "en" ? "System Core Coordinates Portal" : "Portal koordinata sustava"}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-1 rounded-full border border-slate-800 text-gray-400 hover:text-white hover:border-quantum-turquoise/30 transition-all cursor-pointer"
                  aria-label={language === "en" ? "Close Quick-Jump" : "Zatvori brzi skok"}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Search Bar Input */}
              <div className="relative mb-4">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-quantum-turquoise/70" />
                <input
                  ref={inputRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setSelectedIndex(0);
                  }}
                  placeholder={language === "en" ? "Type section coordinates or name..." : "Upišite naziv ili koordinate sekcije..."}
                  className="w-full bg-[#10182E]/90 border border-quantum-turquoise/30 focus:border-quantum-turquoise/85 text-white font-sans text-sm pl-11 pr-16 py-3.5 rounded-xl outline-none shadow-inner transition-all placeholder:text-gray-500 font-light"
                />
                <div className="absolute right-4 top-1/2 -translate-y-1/2 flex items-center gap-1">
                  <span className="text-[9px] font-mono uppercase bg-quantum-turquoise/10 border border-quantum-turquoise/25 text-quantum-turquoise px-2 py-0.5 rounded font-bold">
                    {filteredSections.length}
                  </span>
                  <span className="text-[9px] font-mono text-gray-500 uppercase hidden sm:inline">
                    {language === "en" ? "found" : "pronađeno"}
                  </span>
                </div>
              </div>

              {/* Search Results */}
              <div
                ref={resultsContainerRef}
                className="flex-1 overflow-y-auto space-y-2 pr-1 custom-scrollbar min-h-[250px] max-h-[50vh]"
              >
                {filteredSections.length > 0 ? (
                  filteredSections.map((section, idx) => {
                    const IconComp = section.icon;
                    const isActive = idx === selectedIndex;
                    return (
                      <div
                        key={section.id}
                        onClick={() => triggerJump(section.id)}
                        onMouseEnter={() => setSelectedIndex(idx)}
                        className={`flex items-start gap-4 p-3.5 rounded-xl border transition-all duration-200 cursor-pointer ${
                          isActive
                            ? "bg-quantum-turquoise/10 border-quantum-turquoise/60 shadow-[0_0_15px_rgba(0,214,255,0.1)]"
                            : "bg-[#10182E]/40 border-slate-800/40 hover:bg-[#10182E]/70 hover:border-slate-800/90"
                        }`}
                      >
                        <div
                          className={`p-2 rounded-lg border shrink-0 transition-all ${
                            isActive
                              ? "bg-quantum-turquoise/20 border-quantum-turquoise/50 text-quantum-turquoise"
                              : "bg-slate-900/50 border-slate-800 text-gray-400"
                          }`}
                        >
                          <IconComp className="w-5 h-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4
                            className={`text-xs md:text-sm font-semibold tracking-wide transition-all ${
                              isActive ? "text-quantum-turquoise" : "text-gray-200"
                            }`}
                          >
                            {language === "en" ? section.nameEn : section.nameHr}
                          </h4>
                          <p className="text-[10px] md:text-xs text-gray-400 mt-1 font-sans font-light truncate">
                            {language === "en" ? section.descEn : section.descHr}
                          </p>
                        </div>

                        {isActive && (
                          <div className="flex items-center gap-1 text-[9px] font-mono text-quantum-turquoise shrink-0 self-center bg-quantum-turquoise/5 border border-quantum-turquoise/20 px-1.5 py-0.5 rounded">
                            <span>JUMP</span>
                            <CornerDownLeft className="w-2.5 h-2.5" />
                          </div>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <div className="py-12 text-center text-gray-500 font-mono text-xs">
                    {language === "en" ? "No cosmic coordinates match your query" : "Nema kozmičkih koordinata koje odgovaraju pretrazi"}
                  </div>
                )}
              </div>

              {/* Navigation Shortcuts Info Bar */}
              <div className="mt-4 pt-3 border-t border-slate-800/80 flex items-center justify-between text-[9px] font-mono text-gray-500 uppercase">
                <div className="flex items-center gap-3">
                  <span className="flex items-center gap-1">
                    <kbd className="bg-slate-900 px-1.5 py-0.5 rounded border border-slate-800">↑↓</kbd>
                    {language === "en" ? "Navigate" : "Kretanje"}
                  </span>
                  <span className="flex items-center gap-1">
                    <kbd className="bg-slate-900 px-1.5 py-0.5 rounded border border-slate-800">Enter</kbd>
                    {language === "en" ? "Jump" : "Skoči"}
                  </span>
                </div>
                <div>
                  <span className="flex items-center gap-1">
                    <kbd className="bg-slate-900 px-1.5 py-0.5 rounded border border-slate-800">Esc</kbd>
                    {language === "en" ? "Close" : "Zatvori"}
                  </span>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
}
